import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { Download, Refresh, Visibility } from '@mui/icons-material';

const ReporteCnbs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reporteData, setReporteData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabActual, setTabActual] = useState(0);
  const [filtros, setFiltros] = useState({
    semestre: '',
    año: new Date().getFullYear(),
    tipoDemanda: '',
    naturaleza: ''
  });

  // Reemplazar el estado del contador por una ref para mantener el valor entre renders
  const contadorAgrupadoRef = useRef({});

  const API_URL = "https://bei360.injupemp.gob.hn:8444/api/tramite/listar/";

  // Catálogos actualizados según especificaciones
  const catalogos = {
    naturaleza: {
      '1': '1', // A Favor
      '2': '2'  // En Contra
    },
    tipoDemandante: {
      '1': '01', // Persona Natural
      '2': '02', // Persona Jurídica
      '3': '03', // Proveedor
      '4': '04', // Colaborador
      '5': '99'  // Otros
    },
    tipoDemanda: {
      '1': '01', // Daños y Perjuicios
      '2': '02', // Ejecución de Garantías
      '3': '03', // Ejecución de Pagos
      '4': '04', // Ejecución de Títulos
      '5': '05', // Ejecutiva
      '6': '06', // Extra Contractual
      '7': '07', // Laboral
      '8': '08', // Monitorio
      '9': '09', // Nulidad de Instrumentos
      '10': '10', // Responsabilidad Contractual
      '11': '99'  // Otros
    },
    juzgado: {
      '1': '01', // Juzgado Civil
      '2': '02', // Juzgado Primero de Letras Departamental
      '3': '03', // Juzgado Primero de Letras Seccional
      '4': '04', // Ministerio Público
      '5': '99'  // Otros
    },
    moneda: {
      '1': '1', // Lempiras
      '2': '2', // Dólares
      '3': '3'  // Euros
    },
    resultadoInstancia: {
      '1': '1', // Favorable
      '2': '2', // Desfavorable
      '3': '3'  // No aplica
    },
    riesgoCredito: {
      '1': '0', // Sí
      '2': '1'  // No
    },
    resultadoCierre: {
      '1': '1', // Favorable
      '2': '2', // Desfavorable
      '3': '3', // Conciliación entre las partes
      '4': '4' , // Proceso desistido o abandono de proceso
      '5': '5'  // Otros
    }
  };

  // Motivos por tipo de demanda agrupada
  const motivosAgrupados = {
    '02': 'Lote agrupado de demandas de Ejecución de Garantías asociadas a riesgo de crédito',
    '03': 'Lote agrupado de demandas de Ejecución de Pagos asociadas a riesgo de crédito',
    '04': 'Lote agrupado de demandas de Ejecución de Títulos asociadas a riesgo de crédito',
    '05': 'Lote agrupado de demandas Ejecutivas asociadas a riesgo de crédito'
  };

  // Generar opciones de semestres (últimos 3 años)
  const generarOpcionesSemestres = () => {
    const añoActual = new Date().getFullYear();
    const opciones = [];
    
    for (let año = añoActual; año >= añoActual - 2; año--) {
      opciones.push(
        { value: `1-${año}`, label: `Primer Semestre ${año}` },
        { value: `2-${año}`, label: `Segundo Semestre ${año}` }
      );
    }
    
    return opciones;
  };

  // Seleccionar semestre actual
  const seleccionarSemestreActual = (numeroSemestre) => {
    const año = new Date().getFullYear();
    setFiltros(prev => ({
      ...prev,
      semestre: `${numeroSemestre}-${año}`
    }));
  };

  // Obtener texto descriptivo del período
  const obtenerTextoPeriodo = (semestre) => {
    if (!semestre) return '';
    const [numero, año] = semestre.split('-');
    if (numero === '1') {
      return `1 de enero de ${año} al 30 de junio de ${año}`;
    } else {
      return `1 de julio de ${año} al 31 de diciembre de ${año}`;
    }
  };

  // Obtener fecha de corte a partir del semestre
  const obtenerFechaCorte = (semestre) => {
    if (!semestre) return '';
    const [numero, año] = semestre.split('-');
    if (numero === '1') {
      return `${año}-06-30`;
    } else {
      return `${año}-12-31`;
    }
  };

  // Obtener fecha formateada para nombre de archivo (AAAAMMDD)
  const obtenerFechaArchivo = (semestre) => {
    if (!semestre) return '';
    const [numero, año] = semestre.split('-');
    if (numero === '1') {
      return `${año}0630`;
    } else {
      return `${año}1231`;
    }
  };

  // Inicializar semestre actual por defecto al cargar el componente
  useEffect(() => {
    const hoy = new Date();
    const año = hoy.getFullYear();
    const mes = hoy.getMonth() + 1;
    const semestre = mes <= 6 ? 1 : 2;
    
    setFiltros(prev => ({
      ...prev,
      semestre: `${semestre}-${año}`
    }));
  }, []);

  // Función para formatear montos
  const formatearMonto = (monto) => {
    if (!monto) return '0.00';
    const valor = parseFloat(monto);
    return isNaN(valor) ? '0.00' : valor.toFixed(2);
  };

  // Función para truncar texto
  const truncarTexto = (texto, longitud) => {
    if (!texto) return '';
    const textoStr = texto.toString();
    return textoStr.length > longitud ? textoStr.substring(0, longitud) : textoStr;
  };

  // Función para formatear fecha AAAAMMDD
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    try {
      const date = new Date(fecha);
      return date.toISOString().slice(0, 10).replace(/-/g, '');
    } catch {
      return '';
    }
  };

  // Convertir moneda extranjera a lempiras
  const convertirALempiras = (monto, moneda, tipoCambio) => {
    if (!monto) return '0.00';
    const valor = parseFloat(monto);
    if (isNaN(valor)) return '0.00';

    if (moneda === '1') { // Lempiras
      return valor.toFixed(2);
    } else { // Moneda extranjera
      const cambio = parseFloat(tipoCambio || 1);
      return (valor * cambio).toFixed(2);
    }
  };

  // Determinar si es demanda agrupada según especificaciones (CORREGIDA)
  const esDemandaAgrupada = (demanda) => {
    // Naturaleza 1 (A Favor) + Tipos 2,3,4,5 + Asociada a riesgo crédito
    // EN BD: riesgocredito = 1 significa "Sí está asociada a riesgo crédito"
    return demanda.naturaleza === 1 && 
           [2, 3, 4, 5].includes(demanda.tipodemanda) && 
           demanda.riesgocredito === 1; 
  };

  // Obtener el siguiente número para demandas agrupadas (GLOBAL por año)
  const obtenerSiguienteNumeroAgrupado = (año) => {
    // Usar ref en lugar de estado para evitar problemas de asincronía
    const nuevoContador = (contadorAgrupadoRef.current[año] || 0) + 1;
    contadorAgrupadoRef.current = {
      ...contadorAgrupadoRef.current,
      [año]: nuevoContador
    };
    return nuevoContador;
  };

  // Obtener juzgado predominante para observaciones
  const obtenerJuzgadoPredominante = (demandas) => {
    const conteoJuzgados = {};
    demandas.forEach(demanda => {
      const juzgado = demanda.juzgado || '5'; // 5: Otros por defecto
      conteoJuzgados[juzgado] = (conteoJuzgados[juzgado] || 0) + 1;
    });

    const juzgadoPredominante = Object.keys(conteoJuzgados).reduce((a, b) => 
      conteoJuzgados[a] > conteoJuzgados[b] ? a : b
    );

    return {
      codigo: catalogos.juzgado[juzgadoPredominante] || '99',
      descripcion: juzgadoPredominante === '1' ? 'Juzgado Civil' :
                  juzgadoPredominante === '2' ? 'Juzgado Primero de Letras Departamental' :
                  juzgadoPredominante === '3' ? 'Juzgado Primero de Letras Seccional' :
                  juzgadoPredominante === '4' ? 'Ministerio Público' : 'Otros'
    };
  };

  // Validar si una demanda cerrada cumple con los criterios
  const esDemandaCerradaValida = (demanda) => {
    // Solo demandas individuales (no agrupadas)
    if (esDemandaAgrupada(demanda)) {
      return false;
    }

    // Criterios de inclusión:
    // 1. Demandas en contra (naturaleza = 2) O
    // 2. Demandas a favor (naturaleza = 1) que NO estén asociadas a riesgo de crédito (riesgocredito = 1)
    const esValida = 
      demanda.naturaleza === 2 || 
      (demanda.naturaleza === 1 && demanda.riesgocredito === 2);

    if (!esValida) {
      console.log('Demanda excluida - No cumple criterios:', {
        numero: demanda.numerodemanda,
        naturaleza: demanda.naturaleza,
        riesgoCredito: demanda.riesgocredito,
        estado: demanda.estado
      });
    }

    return esValida;
  };

  // Obtener datos de la API
  const obtenerDatos = async () => {
    if (!filtros.semestre) {
      setError('Por favor seleccione un semestre');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setReporteData([]);

    try {
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Formato de respuesta inválido');
      }

      console.log('Total demandas obtenidas:', data.length);

      // Obtener fechas del período semestral seleccionado
      const fechaCorte = obtenerFechaCorte(filtros.semestre);
      const [numeroSemestre, año] = filtros.semestre.split('-');
      const fechaInicioPeriodo = numeroSemestre === '1' 
        ? `${año}-01-01` 
        : `${año}-07-01`;

      // Filtrar por estado y período según la pestaña activa
      let datosFiltrados = data;
      
      if (tabActual === 0) { 
        // DEMANDAS EN EJECUCIÓN: SOLO estado 'En proceso' 
        datosFiltrados = data.filter(d => 
          d.estado === 'En proceso' && 
          d.fechapresentacion && 
          d.fechapresentacion >= fechaInicioPeriodo && 
          d.fechapresentacion <= fechaCorte
        );
        console.log(`Demandas en Proceso entre ${fechaInicioPeriodo} y ${fechaCorte}:`, datosFiltrados.length);
      } else { 
        // DEMANDAS CERRADAS: Estado 'Cerrado' Y fecha de cierre dentro del período
        datosFiltrados = data.filter(d => 
          d.estado === 'Cerrado' && 
          d.fechacierre && 
          d.fechacierre >= fechaInicioPeriodo && 
          d.fechacierre <= fechaCorte
        );
        console.log(`Demandas Cerradas entre ${fechaInicioPeriodo} y ${fechaCorte}:`, datosFiltrados.length);

        // Aplicar validaciones específicas para demandas cerradas
        const totalAntesFiltro = datosFiltrados.length;
        datosFiltrados = datosFiltrados.filter(d => esDemandaCerradaValida(d));
        console.log(`Demandas Cerradas después de validaciones: ${datosFiltrados.length} de ${totalAntesFiltro}`);
      }

      // Aplicar filtros adicionales
      if (filtros.tipoDemanda) {
        datosFiltrados = datosFiltrados.filter(d => 
          d.tipodemanda === parseInt(filtros.tipoDemanda)
        );
      }
      if (filtros.naturaleza) {
        datosFiltrados = datosFiltrados.filter(d => 
          d.naturaleza === parseInt(filtros.naturaleza)
        );
      }

      console.log('Demandas después de filtros:', datosFiltrados.length);

      // Procesar datos según el tipo de reporte
      if (tabActual === 0) {
        procesarDatosEjecucion(datosFiltrados, fechaInicioPeriodo, fechaCorte);
      } else {
        procesarDatosCerradas(datosFiltrados);
      }
      
    } catch (err) {
      setError(`Error al obtener datos: ${err.message}`);
      console.error('Error detallado:', err);
    } finally {
      setLoading(false);
    }
  };

  // Procesar datos para Demandas en Ejecución con nueva lógica de agrupación
  const procesarDatosEjecucion = (demandas, fechaInicioPeriodo, fechaCorte) => {
  console.log(`=== INICIANDO PROCESAMIENTO EJECUCIÓN ===`);
  console.log(`Total demandas a procesar: ${demandas.length}`);
  
  const lineasReporte = [];
  const demandasAgrupadas = {
    '02': [], // Ejecución de Garantías
    '03': [], // Ejecución de Pagos
    '04': [], // Ejecución de Títulos
    '05': []  // Ejecutiva
  };

  const demandasIndividuales = [];

  // Separar demandas según criterios de agrupación
  demandas.forEach(demanda => {
    if (esDemandaAgrupada(demanda)) {
      // Agrupar por tipo de demanda
      const tipo = catalogos.tipoDemanda[demanda.tipodemanda];
      console.log(`✓ DEMANDA AGRUPABLE: ${demanda.numerodemanda} → Tipo: ${tipo}`);
      
      if (tipo && demandasAgrupadas[tipo]) {
        demandasAgrupadas[tipo].push(demanda);
      }
    } else {
      // Demandas individuales (en contra, sin riesgo crédito, o tipos diferentes)
      console.log(`✗ DEMANDA NO AGRUPABLE: ${demanda.numerodemanda}`);
      demandasIndividuales.push(demanda);
    }
  });

  // Procesar demandas individuales (las que nunca son agrupables)
  demandasIndividuales.forEach(demanda => {
    lineasReporte.push(formatearLineaIndividualEjecucion(demanda));
  });

  // Procesar demandas agrupables
  console.log(`\n=== PROCESANDO DEMANDAS AGRUPABLES ===`);
  Object.keys(demandasAgrupadas).forEach(tipo => {
    const grupo = demandasAgrupadas[tipo];
    
    if (grupo.length > 1) {
      console.log(`Grupo ${tipo}: ${grupo.length} demandas → Crear línea AGRUPADA`);
      
      const lineaAgrupada = formatearLineaAgrupadaEjecucion(grupo, tipo);
      if (lineaAgrupada) {
        console.log(`✓ Línea AGRUPADA creada: ${lineaAgrupada.numeroDemanda}`);
        lineasReporte.push(lineaAgrupada);
      }
    } else if (grupo.length === 1) {
      // Si solo hay 1 demanda agrupable, tratarla como INDIVIDUAL
      console.log(`Grupo ${tipo}: 1 demanda → Tratar como INDIVIDUAL`);
      lineasReporte.push(formatearLineaIndividualEjecucion(grupo[0]));
    }
  });

  if (lineasReporte.length === 0) {
    const mensajeVacio = 'No se encontraron demandas en ejecución válidas para generar el reporte.';
    setReporteData([]);
    setSuccess(mensajeVacio);
    return;
  }

  // Estadísticas actualizadas
  const stats = {
    gruposCreados: 0,
    demandasEnGrupos: 0,
    individualesDeNoAgrupables: demandasIndividuales.length,
    individualesDeAgrupablesSolas: 0,
    agrupadas: 0
  };

  // Contar grupos creados y demandas en grupos
  Object.keys(demandasAgrupadas).forEach(tipo => {
    const grupo = demandasAgrupadas[tipo];
    if (grupo.length > 1) {
      stats.gruposCreados++;
      stats.demandasEnGrupos += grupo.length;
      stats.agrupadas++;
    } else if (grupo.length === 1) {
      stats.individualesDeAgrupablesSolas++;
    }
  });

  console.log(`\n=== ESTADÍSTICAS FINALES ===`);
  console.log(`Total líneas: ${lineasReporte.length}`);
  console.log(`Líneas individuales: ${stats.individualesDeNoAgrupables + stats.individualesDeAgrupablesSolas}`);
  console.log(`Líneas agrupadas: ${stats.agrupadas}`);
  console.log(`Grupos creados: ${stats.gruposCreados}`);
  console.log(`Demandas en grupos: ${stats.demandasEnGrupos}`);
  console.log(`Demandas agrupables tratadas como individuales (grupos de 1): ${stats.individualesDeAgrupablesSolas}`);

  setReporteData(lineasReporte);

  const mensajeExito = `Reporte de Demandas en Ejecución generado correctamente. Total líneas: ${lineasReporte.length}`;
  setSuccess(mensajeExito);
};

  // Formatear línea individual para ejecución
  const formatearLineaIndividualEjecucion = (demanda) => {
    const fechaCorte = obtenerFechaCorte(filtros.semestre);
    const montoDemandadoLps = convertirALempiras(
      demanda.montodemandado, 
      demanda.moneda, 
      demanda.tipocambio
    );

    const montoEstimacionLps = convertirALempiras(
      demanda.montoestimacion,
      demanda.moneda,
      demanda.tipocambio
    );

    const gastosLegalesLps = convertirALempiras(
      demanda.montogastoslegales,
      demanda.moneda,
      demanda.tipocambio
    );

    const campos = [
      '11', // Tipo de Institución
      '01', // Código de Institución
      formatearFecha(fechaCorte), // Fecha de Reporte
      truncarTexto(demanda.numerodemanda, 50), // Número Demanda
      truncarTexto(demanda.numeroexpediente, 50), // Número Expediente
      formatearFecha(demanda.fechapresentacion), // Fecha Presentación
      catalogos.naturaleza[demanda.naturaleza] || '', // Naturaleza
      truncarTexto(demanda.nombredemandado, 100), // Nombre Demandado
      truncarTexto(demanda.nombredemandante, 100), // Nombre Demandante
      catalogos.tipoDemandante[demanda.tipodemandante] || '99', // Tipo Demandante
      catalogos.tipoDemanda[demanda.tipodemanda] || '99', // Tipo Demanda
      truncarTexto(demanda.motivodemanda, 500), // Motivo Demanda
      catalogos.juzgado[demanda.juzgado] || '99', // Juzgado
      truncarTexto(demanda.accioninstitucion, 500), // Acción Institución
      formatearMonto(montoDemandadoLps), // Monto Demandado
      '1', // Moneda (Lempiras)
      '1.0000', // Tipo Cambio
      catalogos.resultadoInstancia[demanda.resultadoprimerainstancia] || '',
      catalogos.resultadoInstancia[demanda.resultadosegundainstancia] || '',
      formatearFecha(demanda.fechaactuacion), // Fecha Actuación
      formatearMonto(montoEstimacionLps), // Monto Estimación
      (parseFloat(montoEstimacionLps) > 0) ? truncarTexto(demanda.cuentaestimacion, 18) : '', // Cuenta Estimación
      '0.00', // Monto Provisión
      '', // Cuenta Provisión
      formatearMonto(gastosLegalesLps), // Gastos Legales
      (parseFloat(gastosLegalesLps) > 0) ? truncarTexto(demanda.cuentagastos, 18) : '', // Cuenta Gastos
      catalogos.riesgoCredito[demanda.riesgocredito] || '', // Riesgo Crédito
      '1', // Cantidad Demandas
      truncarTexto(demanda.observaciones, 500) // Observaciones
    ];

    return {
      tipo: 'individual',
      tipoReporte: 'ejecucion',
      datos: demanda,
      linea: campos.join('|'),
      campos: campos,
      numeroDemanda: demanda.numerodemanda,
      montoDemandado: montoDemandadoLps,
      estado: demanda.estado,
      naturaleza: demanda.naturaleza,
      riesgoCredito: demanda.riesgocredito
    };
  };

  // Formatear línea AGRUPADA para ejecución (MODIFICADA para aceptar 1 o más demandas)
  const formatearLineaAgrupadaEjecucion = (demandas, tipo) => {
    console.log(`Formateando línea agrupada tipo ${tipo} con ${demandas.length} demandas`);
    
    // IMPORTANTE: Permitir grupos con 1 sola demanda según el requerimiento
    if (demandas.length <= 1) {
    console.error('Error: Intentando crear línea agrupada con menos de 2 demandas');
    return null;
  }

    const fechaCorte = obtenerFechaCorte(filtros.semestre);
    const [semestre, año] = filtros.semestre.split('-');
    
    // Contador GLOBAL por año
    const contador = obtenerSiguienteNumeroAgrupado(año);
    const numeroDemanda = `AGR-${contador}-${año}`;

    console.log(`Número de demanda agrupada: ${numeroDemanda}`);

    // Calcular totales en lempiras
    const totales = demandas.reduce((acc, demanda) => {
      const montoDemandadoLps = parseFloat(convertirALempiras(
        demanda.montodemandado, 
        demanda.moneda, 
        demanda.tipocambio
      )) || 0;
      
      const estimacionLps = parseFloat(convertirALempiras(
        demanda.montoestimacion,
        demanda.moneda,
        demanda.tipocambio
      )) || 0;
      
      const gastosLps = parseFloat(convertirALempiras(
        demanda.montogastoslegales,
        demanda.moneda,
        demanda.tipocambio
      )) || 0;

      return {
        montoDemandado: acc.montoDemandado + montoDemandadoLps,
        estimacion: acc.estimacion + estimacionLps,
        gastosLegales: acc.gastosLegales + gastosLps
      };
    }, { montoDemandado: 0, estimacion: 0, gastosLegales: 0 });

    // Obtener acciones concatenadas (truncar a 500 caracteres)
    const accionesConcatenadas = demandas
      .map(d => d.accioninstitucion)
      .filter(Boolean)
      .join('; ');
    
    const accionesTruncadas = truncarTexto(accionesConcatenadas, 500);

    // Obtener juzgado predominante para observaciones
    const juzgadoPredominante = obtenerJuzgadoPredominante(demandas);

    // Obtener cuenta de estimación (primera demanda con estimación > 0)
    const cuentaEstimacion = demandas.find(d => 
      parseFloat(convertirALempiras(d.montoestimacion, d.moneda, d.tipocambio)) > 0
    )?.cuentaestimacion || '';

    // Obtener cuenta de gastos (primera demanda con gastos > 0)
    const cuentaGastos = demandas.find(d => 
      parseFloat(convertirALempiras(d.montogastoslegales, d.moneda, d.tipocambio)) > 0
    )?.cuentagastos || '';

    // Campos EXACTAMENTE según especificaciones
    const campos = [
      '01', // 1. Tipo de Institución
      '01', // 2. Código de Institución
      formatearFecha(fechaCorte), // 3. Fecha de Reporte de Datos
      numeroDemanda, // 4. Número de la Demanda (AGR-incremental-año)
      '', // 5. Número de Expediente (VACÍO)
      '', // 6. Fecha de Presentación (VACÍO)
      '1', // 7. Naturaleza de Demanda (1: A Favor)
      'Clientes', // 8. Nombre del Demandado ("Clientes")
      '', // 9. Nombre del Demandante (VACÍO)
      '99', // 10. Tipo de Demandante (99: Otros)
      tipo, // 11. Tipo de Demanda (02,03,04,05)
      motivosAgrupados[tipo] || 'Lote agrupado de demandas', // 12. Motivo de la Demanda
      '99', // 13. Juzgado (99: Otros)
      accionesTruncadas, // 14. Acción de la Institución (concatenada y truncada)
      formatearMonto(totales.montoDemandado), // 15. Valor o Monto Demandado (sumatoria)
      '1', // 16. Tipo de Moneda (1: Lempiras)
      '1.0000', // 17. Tipo de Cambio (1.0000)
      '3', // 18. Resultado Primera Instancia (3: No aplica)
      '3', // 19. Resultado Segunda Instancia (3: No aplica)
      '', // 20. Fecha de Actuación (VACÍO)
      formatearMonto(totales.estimacion), // 21. Monto de Estimación (sumatoria)
      (totales.estimacion > 0) ? truncarTexto(cuentaEstimacion, 18) : '', // 22. Cuenta Contable - Estimación
      '0.00', // 23. Monto Provisión (0.00)
      '', // 24. Cuenta Contable - Provisión (VACÍO)
      formatearMonto(totales.gastosLegales), // 25. Monto Gastos Legales (sumatoria)
      (totales.gastosLegales > 0) ? truncarTexto(cuentaGastos, 18) : '', // 26. Cuenta Contable - Gastos
      '0', // 27. Demanda Asociada con Riesgo Crédito (0: Sí)
      demandas.length.toString(), // 28. Cantidad de Demandas (número de demandas agrupadas - AHORA PUEDE SER 1 O MÁS)
      `Tipo de demandante: 99 (Otros). Juzgado predominante: ${juzgadoPredominante.codigo} (${juzgadoPredominante.descripcion}).` // 29. Observaciones
    ];

    return {
      tipo: 'agrupada',
      tipoReporte: 'ejecucion',
      datos: demandas,
      linea: campos.join('|'),
      campos: campos,
      totalDemandas: demandas.length,
      totalMonto: totales.montoDemandado,
      numeroDemanda: numeroDemanda,
      tipoDemanda: tipo,
      naturaleza: 1, // Siempre "A Favor" para agrupadas
      riesgoCredito: 1 // Siempre "Sí" para agrupadas (valor BD = 1)
    };
  };

// Procesar datos para Demandas Cerradas con validaciones específicas
  const procesarDatosCerradas = (demandas) => {
    // Filtrar solo demandas individuales válidas según criterios
    const demandasValidas = demandas.filter(demanda => esDemandaCerradaValida(demanda));
    
    console.log(`Demandas cerradas válidas: ${demandasValidas.length} de ${demandas.length} totales`);

    // Generar estadísticas de exclusión
    const estadisticas = {
      total: demandas.length,
      incluidas: demandasValidas.length,
      excluidas: demandas.length - demandasValidas.length,
      porNaturaleza: {
        aFavorConRiesgo: demandas.filter(d => d.naturaleza === 1 && d.riesgocredito === 0).length,
        aFavorSinRiesgo: demandas.filter(d => d.naturaleza === 1 && d.riesgocredito === 1).length,
        enContra: demandas.filter(d => d.naturaleza === 2).length
      }
    };
    if (demandasValidas.length === 0) {
            const mensajeVacio = 'No se encontraron demandas cerradas válidas para generar el reporte.';
            setReporteData([]); 
            setSuccess(mensajeVacio);
            return; 
        }
    const lineasReporte = demandasValidas.map(demanda => formatearLineaCerrada(demanda));
    setReporteData(lineasReporte);
    
    const periodo = obtenerTextoPeriodo(filtros.semestre);
    
    // Mensaje informativo con estadísticas
    const mensajeEstadisticas = `
      Reporte de Demandas Cerradas generado `.replace(/\s+/g, ' ').trim();

    setSuccess(mensajeEstadisticas);
  };

  // Formatear línea individual para demandas cerradas (NUEVO FORMATO - 14 campos)
  const formatearLineaCerrada = (demanda) => {
    const fechaCorte = obtenerFechaCorte(filtros.semestre);
    
    // Convertir montos a lempiras usando los datos de la API
    const valorDefinitivoLps = convertirALempiras(
      demanda.valordefinitivoconciliado || '0',
      demanda.moneda || '1',
      demanda.tipocambio || '1.0000'
    );

    const gastosLegalesLps = convertirALempiras(
      demanda.montogastoslegales || '0',
      demanda.moneda || '1',
      demanda.tipocambio || '1.0000'
    );

    // Campos para demandas cerradas
    const campos = [
      '11', // 1. Tipo de Institución (11 - fijo)
      '01', // 2. Código de Institución (01 - fijo)
      formatearFecha(fechaCorte), // 3. Fecha de Reporte de Datos (fecha de corte del semestre)
      truncarTexto(demanda.numerodemanda || '', 50), // 4. Número de la Demanda
      truncarTexto(demanda.numeroexpediente || '', 50), // 5. Número de Expediente
      formatearFecha(demanda.fechacierre), // 6. Fecha de Finalización de la Demanda
      catalogos.naturaleza[demanda.naturaleza] || '', // 7. Naturaleza de Demanda
      truncarTexto(demanda.nombredemandado || '', 100), // 8. Nombre del Demandado
      truncarTexto(demanda.nombredemandante || '', 100), // 9. Nombre del Demandante
      catalogos.resultadoCierre[demanda.resultadocierre] || '1', // 10. Resultado de la Demanda
      truncarTexto(demanda.descripcioncierre || '', 500), // 11. Descripción de Cierre de la Demanda
      formatearMonto(valorDefinitivoLps), // 12. Valor Definitivo o Conciliado
      (parseFloat(valorDefinitivoLps) > 0) ? truncarTexto(demanda.cuentavalordefinitivo || '', 18) : '', // 13. Cuenta Contable – Valor Definitivo o Conciliado
      formatearMonto(gastosLegalesLps) // 14. Monto Gastos Legales u Honorarios
    ];

    return {
      tipo: 'cerrada',
      tipoReporte: 'cerradas',
      datos: demanda,
      linea: campos.join('|'),
      campos: campos,
      numeroDemanda: demanda.numerodemanda,
      resultado: demanda.resultadocierre,
      valorDefinitivo: valorDefinitivoLps,
      estado: demanda.estado,
      naturaleza: demanda.naturaleza,
      riesgoCredito: demanda.riesgocredito
    };
  };

  const descargarArchivo = () => {
    if (reporteData.length === 0) {
      setError('No hay datos para descargar');
      return;
    }

    const contenido = reporteData.map(item => item.linea).join('\n');
    
    // Nomenclatura diferente según el tipo de reporte
    let nombreArchivo = '';
    const fechaArchivo = obtenerFechaArchivo(filtros.semestre);
    
    if (tabActual === 0) {
      // Para ejecución: formato anterior
      nombreArchivo = `ROPSSDE${fechaArchivo}.ASC`;
    } else {
      // Para cerradas:
      nombreArchivo = `ROPSSDC${fechaArchivo}.ASC`;
    }
    
    const blob = new Blob([contenido], { type: 'text/plain; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setSuccess(`Archivo descargado: ${nombreArchivo}`);
  };

  const handleFiltrosChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabActual(newValue);
    setReporteData([]);
    setSuccess('');
    setError('');
  };

  return (
    <Box sx={{ p: 2 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 3,
            py: 2,
            background: 'white',
            color: 'black'
          }}>
            <Typography variant="h4" fontWeight="bold" sx={{ textAlign: 'center' }}>
               Reporte de Demandas
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            my: 4,
            position: 'sticky', 
            top: 0, 
            zIndex: 1,
            backgroundColor: 'background.paper',
            py: 1
          }}>
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Tabs 
                value={tabActual} 
                onChange={handleTabChange} 
                centered
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 3,
                    minWidth: 180,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: 2,
                      mx: 0.5
                    }
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: tabActual === 0 ? 'white' : 'warning.main' 
                      }} />
                      En Ejecución
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: tabActual === 1 ? 'white' : 'error.main' 
                      }} />
                      Cerradas
                    </Box>
                  } 
                />
              </Tabs>
            </Paper>
          </Box>

          {/* Filtros */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                width: 4, 
                height: 20, 
                backgroundColor: 'primary.main', 
                borderRadius: 2,
                mr: 2
              }} />
              <Typography variant="h6" fontWeight="bold">
                 Filtros Semestrales
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Semestre"
                  name="semestre"
                  value={filtros.semestre}
                  onChange={handleFiltrosChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Seleccione el semestre a consultar"
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="">
                    <em>Seleccionar semestre</em>
                  </MenuItem>
                  {generarOpcionesSemestres().map((opcion) => (
                    <MenuItem key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="medium">
                    📅 Períodos semestrales rápidos:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button 
                      color="success"
                      size="small" 
                      variant={filtros.semestre === `1-${new Date().getFullYear()}` ? "contained" : "outlined"}
                      onClick={() => seleccionarSemestreActual(1)}
                      startIcon={<Box sx={{ fontSize: '0.8rem' }}>1S</Box>}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'medium'
                      }}
                    >
                      Primer Semestre {new Date().getFullYear()}
                    </Button>
                    <Button 
                      color="warning"
                      size="small" 
                      variant={filtros.semestre === `2-${new Date().getFullYear()}` ? "contained" : "outlined"}
                      onClick={() => seleccionarSemestreActual(2)}
                      startIcon={<Box sx={{ fontSize: '0.8rem' }}>2S</Box>}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'medium'
                      }}
                    >
                      Segundo Semestre {new Date().getFullYear()}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {filtros.semestre && (
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                backgroundColor: '#f0f0f0f1', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.main'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                    Información del Período Seleccionado
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="primary.dark">
                      <strong>Período:</strong> {obtenerTextoPeriodo(filtros.semestre)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="primary.dark">
                      <strong>Fecha de corte:</strong> {obtenerFechaCorte(filtros.semestre)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button 
                onClick={obtenerDatos} 
                variant="contained" 
                disabled={loading || !filtros.semestre}
                startIcon={<Refresh />}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 'bold'
                }}
              >
                Generar Reporte
              </Button>
              <Typography variant="caption" color="textSecondary">
                {filtros.semestre ? "Listo para generar" : "Seleccione un semestre"}
              </Typography>
            </Box>
          </Paper>

          {/* Progress Bar */}
          {loading && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress 
                sx={{ 
                  mb: 1, 
                  height: 6, 
                  borderRadius: 3 
                }} 
              />
              <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', display: 'block' }}>
                Procesando datos, por favor espere...
              </Typography>
            </Box>
          )}

          {/* Botones de acción principales */}
          {reporteData.length > 0 && (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px solid',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                    ✅ Reporte Generado
                  </Typography>
                  <Chip 
                    label={`${reporteData.length} líneas`}
                    size="small"
                    color="success"
                    variant="filled"
                  />
                  {tabActual === 1 && (
                    <Chip 
                      label="Solo individuales válidas"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={descargarArchivo}
                    disabled={loading}
                    sx={{ 
                      borderRadius: 2,
                      fontWeight: 'bold',
                      px: 3
                    }}
                  >
                    Descargar 
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<Visibility />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ 
                      borderRadius: 2,
                      fontWeight: 'medium',
                      px: 3
                    }}
                  >
                    Vista Previa
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
           {/* Alertas */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                borderRadius: 2,
                alignItems: 'center'
              }}
              onClose={() => setError('')}
            >
              <Typography fontWeight="medium">{error}</Typography>
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2, 
                borderRadius: 2,
                alignItems: 'center'
              }}
              onClose={() => setSuccess('')}
            >
              <Typography fontWeight="medium">{success}</Typography>
            </Alert>
          )}
        </CardContent>
      

      {/* Dialog de vista previa */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Visibility />
            <Typography variant="h6" fontWeight="bold">
              Vista Previa del Reporte
            </Typography>
            <Chip 
              label={`${reporteData.length} líneas`}
              size="small"
              sx={{ 
                backgroundColor: 'white', 
                color: 'primary.main',
                fontWeight: 'bold'
              }}
            />
            {tabActual === 1 && (
              <Chip 
                label="Solo individuales válidas"
                size="small"
                sx={{ 
                  backgroundColor: 'white', 
                  color: 'primary.main',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>
          {filtros.semestre && (
            <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
              {tabActual === 0 ? 'En Proceso' : 'Cerradas'} • {filtros.semestre.split('-')[0] === '1' ? 'Primer' : 'Segundo'} Semestre {filtros.semestre.split('-')[1]}

            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 500, borderRadius: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Tipo</TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Número Demanda</TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Naturaleza</TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Riesgo Crédito</TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>
                    {tabActual === 0 ? 'Monto Demandado (Lps)' : 'Valor Definitivo (Lps)'}
                  </TableCell>
                  {tabActual === 0 && <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Demandas Incluidas</TableCell>}
                  {tabActual === 1 && <TableCell sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>Resultado</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {reporteData.slice(0, 100).map((linea, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={linea.tipo} 
                        size="small"
                        color={
                          linea.tipo === 'individual' ? 'success' : 
                          linea.tipo === 'agrupada' ? 'primary' : 'default'
                        }
                        sx={{ fontWeight: 'medium' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                        {linea.numeroDemanda || linea.campos[3]}
                      </Typography>
                      {linea.tipo === 'agrupada' && (
                        <Typography variant="caption" color="textSecondary">
                          Tipo: {linea.tipoDemanda}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={linea.naturaleza === 1 ? 'A Favor' : 'En Contra'} 
                        size="small"
                        color={linea.naturaleza === 1 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={linea.riesgoCredito === 1 ? 'Sí' : 'No'} 
                        size="small"
                        color={linea.riesgoCredito === 1 ? 'warning' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                        L. {tabActual === 0 ? (linea.totalMonto || linea.montoDemandado || '0.00') : (linea.valorDefinitivo || '0.00')}
                      </Typography>
                    </TableCell>
                    {tabActual === 0 && linea.tipo === 'agrupada' && (
                      <TableCell>
                        <Chip 
                          label={`${linea.totalDemandas} demandas`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {tabActual === 1 && (
                      <TableCell>
                        <Chip 
                          label={
                            linea.datos.resultadocierre === 1 ? 'Favorable' :
                            linea.datos.resultadocierre === 2 ? 'Desfavorable' :
                            linea.datos.resultadocierre === 3 ? 'Conciliación' : 'Desistido'
                          }
                          size="small"
                          color={
                            linea.datos.resultadocierre === 1 ? 'success' :
                            linea.datos.resultadocierre === 2 ? 'error' : 'warning'
                          }
                          variant="filled"
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {reporteData.length > 100 && (
            <Box sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.100' }}>
              <Typography variant="body2" color="textSecondary">
                Mostrando 100 de {reporteData.length} líneas • 
                <Typography component="span" color="primary.main" fontWeight="bold">
                  {' '}Use la descarga para ver el archivo completo
                </Typography>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDialogOpen(false)} 
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cerrar
          </Button>
          <Button 
            onClick={descargarArchivo} 
            variant="contained"
            startIcon={<Download />}
            sx={{ borderRadius: 2, fontWeight: 'bold' }}
          >
            Descargar Archivo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReporteCnbs;